import * as React from 'react';
import { StudentVisaMap, StudentVisaDocList, IDocumentsList, BiometricDocumentList, IStudentVisa } from '../../../../types';
import ChecklistSection from '../../../atoms/checklist-section';
import Finger, { Status } from '../../../atoms/finger';
import { getVisible, getDocStatus } from '../../../../utilities/documents';

interface IProps {
  infoOnly: boolean;
  documents: StudentVisaMap;
  warning?: string;
  onResume: (url: string) => void;
  onVisaClick: (sponsorKey: string, docKey: string, infoOnly: boolean) => void;
  onCasClick: (sponsorKey: string, docKey: string, infoOnly: boolean) => void;
  onBiometricClick: (sponsorKey: string, docKey: string, infoOnly: boolean) => void;
}

const StudyVisa = (props: IProps) => {
  const studentVisas = props.documents;
  const infoOnly = props.infoOnly;

  const visaNum = Object.keys(studentVisas).length;
  if (visaNum === 0) {
    return null;
  }

  const containerDocNodes: React.ReactNode[] = [];

  Object.entries(studentVisas).forEach(([studyVisaKey, studyVisa]) => {
    const docNodes: React.ReactNode[] = [];
    let hasDocs = false;
    if (studyVisa.visa) {
      const visibleDocs = getVisible(studyVisa.visa, infoOnly) as StudentVisaDocList[];
      if (visibleDocs.length > 0) {
        hasDocs = true;
        docNodes.push(
          ...visibleDocs.map((studentVisa: StudentVisaDocList, index: number) => {
            const { key: docKey, item } = studentVisa;
            if (item.status === 'REJECTED') {
              return (
                <Finger
                  hideButton={true}
                  reason={item.reason}
                  key={`study-visa-${studyVisaKey}-${docKey}`}
                  id={`study-visa-${studyVisaKey}-${docKey}`}
                  status={Status.Rejected}
                  label={`Previous Student Visa (Vignette) ${
                    visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''
                  }`}
                />
              );
            } else {
              return (
                <Finger
                  key={`study-visa-${studyVisaKey}-${docKey}`}
                  id={`study-visa-${studyVisaKey}-${docKey}`}
                  status={getDocStatus(item.status, infoOnly)}
                  completeLabel={infoOnly ? 'View' : undefined}
                  resolveLabel={infoOnly ? 'Please Confirm' : undefined}
                  buttonLabel={infoOnly ? 'Please review' : undefined}
                  label={`Previous Student Visa (Vignette) ${
                    visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''
                  }`}
                  onClick={() => props.onVisaClick(studyVisaKey, docKey, infoOnly)}
                />
              );
            }
          })
        );
      }
    }

    if (studyVisa.cas) {
      const visibleDocs = getVisible(studyVisa.cas, infoOnly) as IDocumentsList[];
      if (visibleDocs.length > 0) {
        hasDocs = true;
        docNodes.push(
          ...visibleDocs.map((cas: IDocumentsList, index: number) => {
            const { key: docKey, item } = cas;

            if (item.status === 'REJECTED') {
              return (
                <Finger
                  hideButton={true}
                  reason={item.reason}
                  key={`cas-document-${studyVisaKey}-${docKey}`}
                  id={`cas-document-${studyVisaKey}-${docKey}`}
                  status={Status.Rejected}
                  label={`Previous CAS Document ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                />
              );
            } else {
              return (
                <Finger
                  key={`cas-document-${studyVisaKey}-${docKey}`}
                  id={`cas-document-${studyVisaKey}-${docKey}`}
                  status={getDocStatus(item.status, infoOnly)}
                  completeLabel={infoOnly ? 'View' : undefined}
                  resolveLabel={infoOnly ? 'Please Confirm' : undefined}
                  buttonLabel={infoOnly ? 'Please review' : undefined}
                  label={`Previous CAS Document ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                  onClick={() => props.onCasClick(studyVisaKey, docKey, infoOnly)}
                />
              );
            }
          })
        );
      }
    }

    if (studyVisa.biometric) {
      const visibleDocs = getVisible(studyVisa.biometric, infoOnly) as BiometricDocumentList[];
      if (visibleDocs.length > 0) {
        hasDocs = true;
        docNodes.push(
          ...visibleDocs.map((biometric: BiometricDocumentList, index: number) => {
            const { key: docKey, item } = biometric;

            if (item.status === 'REJECTED') {
              return (
                <Finger
                  hideButton={true}
                  reason={item.reason}
                  key={`biometric-${studyVisaKey}-${docKey}`}
                  id={`biometric-${studyVisaKey}-${docKey}`}
                  status={Status.Rejected}
                  label={`Previous Biometric Residence Permit ${
                    visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''
                  }`}
                />
              );
            } else {
              return (
                <Finger
                  key={`biometric-${studyVisaKey}-${docKey}`}
                  id={`biometric-${studyVisaKey}-${docKey}`}
                  status={getDocStatus(item.status, infoOnly)}
                  completeLabel={infoOnly ? 'View' : undefined}
                  resolveLabel={infoOnly ? 'Please Confirm' : undefined}
                  buttonLabel={infoOnly ? 'Please review' : undefined}
                  label={`Previous Biometric Residence Permit ${
                    visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''
                  }`}
                  onClick={() => props.onBiometricClick(studyVisaKey, docKey, infoOnly)}
                />
              );
            }
          })
        );
      }
    }
    if (hasDocs) {
      const visa = Object.values(studyVisa.visa)[0] as IStudentVisa;
      containerDocNodes.push(

        <ChecklistSection
          key={`study-visa-section-${studyVisaKey}`}
          id={`study-visa-section`}
          label={`You indicated you had a ${visa.visaType} student visa in ${visa.year}`}
          edit={() => {
            props.onResume('history/step-1');
          }}
          warning={props.warning}
        >
          {docNodes}
        </ChecklistSection>
      );
    }
  });
  return <>{containerDocNodes}</>;
};

export default StudyVisa;
